import { formatListDate, formatTime } from '@jerome2/shared';

// Opmaak voor de interface: datums, groottes, namen, bedragen, in het Nederlands.

export function listDate(iso: string | null, now = new Date()): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return formatListDate(d, now);
}

export function fullDate(iso: string | null, now = new Date()): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const day = new Intl.DateTimeFormat('nl-NL', { timeZone: 'Europe/Amsterdam', weekday: 'long', day: 'numeric', month: 'long' }).format(d);
  const sameDay = d.toDateString() === now.toDateString();
  return `${sameDay ? 'vandaag' : day} ${formatTime(d)}`;
}

export function relative(iso: string | null, now = new Date()): string {
  if (!iso) return '';
  const ms = now.getTime() - new Date(iso).getTime();
  const s = Math.round(ms / 1000);
  if (s < 45) return 'zojuist';
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min geleden`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} uur geleden`;
  const d = Math.round(h / 24);
  return d === 1 ? 'gisteren' : `${d} dagen geleden`;
}

export function duration(fromIso: string | null, toIso: string | null): string {
  if (!fromIso || !toIso) return '';
  const s = Math.max(0, Math.round((new Date(toIso).getTime() - new Date(fromIso).getTime()) / 1000));
  if (s < 60) return `${s} seconden werk`;
  const m = Math.floor(s / 60);
  return `${m} min ${s % 60} s werk`;
}

/** Hoe lang een beurt van Jerome al loopt (de werkerskolom, ticket 30961): "8 s", "1 min 15 s", vanaf een uur "1 u 2 min". */
export function runningFor(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ${s % 60} s`;
  return `${Math.floor(m / 60)} u ${m % 60} min`;
}

export function fileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
}

export function money(usd: number): string {
  return `$${usd.toFixed(2).replace('.', ',')}`;
}

/** "Tom Bakker" → "Tom"; "tom.bakker@haarlem.nl" → "tom.bakker". */
export function firstName(name: string | null | undefined, address?: string | null): string {
  const n = (name ?? '').trim();
  if (n && !n.includes('@')) return n.split(/\s+/)[0] ?? n;
  return (address ?? n).split('@')[0] ?? '';
}

/** Organisatie uit een adres: "tom@haarlem.nl" → "haarlem.nl". */
export function orgOf(address: string | null | undefined): string {
  const domain = (address ?? '').split('@')[1] ?? '';
  return domain.replace(/^(mail|smtp|outlook)\./, '');
}

export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join('');
}

export function fileKind(name: string, contentType: string | null): string {
  const ext = (name.split('.').pop() ?? '').toLowerCase();
  if (ext === 'pdf' || contentType === 'application/pdf') return 'PDF';
  if (['xlsx', 'xls', 'csv'].includes(ext)) return 'XLS';
  if (['docx', 'doc'].includes(ext)) return 'DOC';
  if (['pptx', 'ppt'].includes(ext)) return 'PPT';
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'heic'].includes(ext) || contentType?.startsWith('image/')) return 'IMG';
  if (['zip', '7z', 'rar'].includes(ext)) return 'ZIP';
  return ext.toUpperCase().slice(0, 4) || 'BST';
}

/**
 * Wat de viewer in beeld kan tonen. Eerst het contenttype (de server controleert de inhoud en geeft een bestand dat niet
 * klopt als `application/octet-stream` door), pas als dat niets zegt de extensie: een "offerte.pdf" die eigenlijk html is,
 * gaat zo naar Downloaden in plaats van in een frame.
 */
export function isViewable(name: string, contentType: string | null): 'pdf' | 'image' | null {
  const type = (contentType ?? '').split(';')[0]!.trim().toLowerCase();
  if (type === 'application/pdf') return 'pdf';
  if (type.startsWith('image/')) return 'image';
  if (type && type !== 'application/octet-stream') return null;
  const kind = fileKind(name, null);
  return kind === 'PDF' ? 'pdf' : kind === 'IMG' ? 'image' : null;
}
